import { useEffect, useMemo, useState } from "react";
import { Button, Popconfirm, Table, Tag } from "antd";
import { CheckCircleOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { AuthenticationState } from "../../interfaces/users";
import { api } from "../../config";
import { SearchFilter } from "../Shared/Components/Filters/SearchFilter";
import { RoutingZoneModal } from "./RoutingZoneModal";
import {
  ModulePermission,
  userHasPermission,
} from "../../utils/helpers/userHelpers";

const formatZoneType = (type: string) =>
  type === "ASSEMBLE" ? "Montagem" : "Frete";

export const RoutingZones = () => {
  const { token, user } = useSelector((state: AuthenticationState) => state);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoutingZone, setSelectedRoutingZone] = useState<any>();
  const {
    data: routingZones = [],
    isFetching,
    refetch,
  } = useQuery("routing-zones", () =>
    api(token).get("/routing-zones", { includeInactive: true })
  );
  const { data: users = [] } = useQuery("routing-zone-users", () =>
    api(token).get("/users")
  );
  const { data: municipalities = [] } = useQuery(
    "routing-zone-municipalities",
    () => api(token).get("/municipalities")
  );
  const { data: neighborhoods = [] } = useQuery(
    "routing-zone-neighborhoods",
    () => api(token).get("/neighborhoods")
  );
  const [tableData, setTableData] = useState<any[]>(routingZones);

  useEffect(() => {
    setTableData(routingZones);
  }, [routingZones]);

  const activeUsers = useMemo(
    () => users.filter((user: any) => user.active !== false),
    [users]
  );
  const userCanManageRegions = userHasPermission(user, {
    region_management: ModulePermission.ADMIN,
  });

  const handleDelete = async (id: number) => {
    try {
      await api(token).delete(`/routing-zones/${id}`);
      toast.success("Zona inativada com sucesso!");
      refetch();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ?? "Ocorreu um erro ao inativar a zona"
      );
    }
  };

  const handleRestore = async (id: number) => {
    try {
      await api(token).put(`/routing-zones/${id}`, { active: true });
      toast.success("Zona reativada com sucesso!");
      refetch();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ?? "Ocorreu um erro ao reativar a zona"
      );
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 80,
    },
    {
      title: (
        <SearchFilter
          placeholder="Nome"
          data={routingZones}
          setData={setTableData}
          field="name"
        />
      ),
      dataIndex: "name",
    },
    {
      title: "Tipo",
      dataIndex: "type",
      render: (type: string) => (
        <Tag color={type === "ASSEMBLE" ? "blue" : "gold"}>
          {formatZoneType(type)}
        </Tag>
      ),
    },
    {
      title: "Usuários",
      dataIndex: "users",
      render: (usersList: any[]) => usersList?.length ?? 0,
    },
    {
      title: "Municípios",
      dataIndex: "municipalities",
      render: (municipalitiesList: any[]) => municipalitiesList?.length ?? 0,
    },
    {
      title: "Status",
      dataIndex: "active",
      render: (active: boolean) => (
        <Tag color={active ? "green" : "default"}>
          {active ? "Ativa" : "Inativa"}
        </Tag>
      ),
    },
    {
      title: "Ações",
      dataIndex: "actions",
      render: (_: any, record: any) => (
        <>
          <Button
            className="m-1 p-2 mt-2"
            size="small"
            icon={<EditOutlined />}
            disabled={!userCanManageRegions}
            onClick={() => {
              setSelectedRoutingZone(record);
              setIsModalOpen(true);
            }}
          />
          <Popconfirm
            title={
              record.active
                ? "Deseja realmente inativar esta zona?"
                : "Deseja realmente reativar esta zona?"
            }
            onConfirm={() =>
              record.active ? handleDelete(record.id) : handleRestore(record.id)
            }
          >
            <Button
              className="m-1 p-1 mt-2"
              size="small"
              danger={record.active}
              shape="circle"
              icon={record.active ? <DeleteOutlined /> : <CheckCircleOutlined />}
              disabled={!userCanManageRegions}
            />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div className="mt-4">
      <RoutingZoneModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRoutingZone(undefined);
        }}
        routingZone={selectedRoutingZone}
        refetch={refetch}
        users={activeUsers}
        municipalities={municipalities}
        neighborhoods={neighborhoods}
      />

      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="mb-1">Zonas</h2>
          <p className="m-0">
            Cadastre zonas de montagem e frete vinculando usuários, municípios e bairros de Curitiba.
          </p>
        </div>
        <Button
          type="primary"
          disabled={!userCanManageRegions}
          onClick={() => {
            setSelectedRoutingZone(undefined);
            setIsModalOpen(true);
          }}
        >
          Nova Zona
        </Button>
      </div>

      <Table
        rowKey="id"
        bordered
        loading={isFetching}
        dataSource={tableData}
        columns={columns}
        pagination={{ position: ["bottomCenter"] }}
      />
    </div>
  );
};
